import { useNavigate } from 'react-router';
import { format, subMonths } from 'date-fns';
import type { Expense, ExpenseCategory } from '@/lib/types';
import { Icons } from '@/components/icons';
import { StatusBadge, CategoryChip } from '@/components/hearthline/primitives';
import { categoryColor } from '@/lib/designAdapters';
import { fmtUSD, fmtUSD0, fmtDate } from '@/lib/format';

interface Props {
  loading: boolean;
  month: { value: string; label: string; start: Date; end: Date };
  stats: {
    totalSpent: number;
    totalTax: number;
    pending: number;
    reimbursed: number;
    donated: number;
    count: number;
    pendingCount: number;
    reimbursedCount: number;
    donatedCount: number;
    momDeltaPct: number | null;
  };
  categoryData: Array<{ category: ExpenseCategory; label: string; icon: string; amount: number }>;
  monthlyTrend: Array<{ month: string; total: number; reimbursed: number }>;
  recentExpenses: Expense[];
  monthExpenses: Expense[];
  onMonthChange: (month: string) => void;
}

export function Dashboard({ loading, month, stats, categoryData, monthlyTrend, recentExpenses, monthExpenses, onMonthChange }: Props) {
  const navigate = useNavigate();

  if (loading) return <div className="empty"><div className="ttl">Loading dashboard…</div></div>;

  if (monthExpenses.length === 0 && recentExpenses.length === 0) {
    return (
      <div className="empty">
        <Icons.Chart size={32} stroke="var(--faint)" />
        <div className="ttl">No expenses yet</div>
        <div>Add your first receipt to see analytics here.</div>
        <button className="btn accent" onClick={() => navigate('/expenses/new')}>
          <Icons.Plus size={14} /> Add receipt
        </button>
      </div>
    );
  }

  const cents = (stats.totalSpent % 1).toFixed(2).slice(2);
  const maxMonth = Math.max(...monthlyTrend.map((m) => m.total), 1);
  const topCats = categoryData.slice(0, 6);
  const maxCat = topCats[0]?.amount || 1;
  const delta = stats.momDeltaPct;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <div className="spacer" style={{ flex: 1 }} />
        <button className="btn sm" onClick={() => onMonthChange(format(subMonths(new Date(`${month.value}-01T00:00:00`), 1), 'yyyy-MM'))}>‹</button>
        <span style={{ minWidth: 130, textAlign: 'center', fontSize: 13, fontWeight: 500 }}>{month.label}</span>
        <button className="btn sm" onClick={() => onMonthChange(format(subMonths(new Date(`${month.value}-01T00:00:00`), -1), 'yyyy-MM'))}>›</button>
      </div>

      <div className="metrics">
        <div className="card metric">
          <div className="lbl">Total spend · {month.label}</div>
          <div className="val">{fmtUSD0(stats.totalSpent)}<span className="cents">.{cents}</span></div>
          {delta != null && (
            <div className={`delta ${delta >= 0 ? 'up' : 'down'}`}>
              {delta >= 0 ? <Icons.TrendUp size={12} /> : <Icons.TrendDown size={12} />}
              {delta >= 0 ? '+' : ''}{delta.toFixed(1)}% vs prev month
            </div>
          )}
        </div>
        <div className="card metric">
          <div className="lbl">Pending reimbursement</div>
          <div className="val" style={{ color: 'oklch(0.5 0.13 75)' }}>{fmtUSD0(stats.pending)}</div>
          <div className="delta">{stats.pendingCount} receipt{stats.pendingCount !== 1 ? 's' : ''} waiting</div>
        </div>
        <div className="card metric">
          <div className="lbl">Reimbursed</div>
          <div className="val" style={{ color: 'oklch(0.45 0.10 150)' }}>{fmtUSD0(stats.reimbursed)}</div>
          <div className="delta">{stats.reimbursedCount} receipt{stats.reimbursedCount !== 1 ? 's' : ''} cleared</div>
        </div>
        <div className="card metric">
          <div className="lbl">Donated supplies</div>
          <div className="val" style={{ color: 'oklch(0.42 0.10 320)' }}>{fmtUSD0(stats.donated)}</div>
          <div className="delta">{stats.donatedCount} community donation{stats.donatedCount !== 1 ? 's' : ''}</div>
        </div>
      </div>

      <div className="dash-grid">
        <div className="card chart-card">
          <div className="card-h">
            <h3>Monthly spend</h3>
            <span className="sub">Last 6 months</span>
            <div className="spacer" />
            <div className="legend" style={{ padding: 0 }}>
              <span className="lg"><span className="sq" style={{ background: 'var(--ink)' }} />Total</span>
              <span className="lg"><span className="sq" style={{ background: 'var(--primary)' }} />Reimbursed</span>
            </div>
          </div>
          <div className="chart-wrap">
            <div className="bars">
              {monthlyTrend.map((m, i) => (
                <div key={i} className="bar-col">
                  <div className="bar-group">
                    <div className="bar" style={{ height: `${(m.total / maxMonth) * 100}%` }} title={`Total ${fmtUSD(m.total)}`} />
                    <div className="bar b2" style={{ height: `${(m.reimbursed / maxMonth) * 100}%` }} title={`Reimbursed ${fmtUSD(m.reimbursed)}`} />
                  </div>
                  <div className="blbl">{m.month}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-h">
            <h3>By category</h3>
            <span className="sub">Top 6</span>
            <div className="spacer" />
            <button className="btn ghost sm" onClick={() => navigate('/reports')}>
              View all <Icons.ChevronRight size={12} />
            </button>
          </div>
          <div>
            {topCats.length === 0 ? (
              <div style={{ padding: 18, fontSize: 13, color: 'var(--muted)' }}>No data for this month</div>
            ) : (
              topCats.map((c) => (
                <div key={c.category} className="cat-row" style={{ color: categoryColor(c.category) }}>
                  <span className="sw" style={{ background: categoryColor(c.category) }} />
                  <span className="nm" style={{ color: 'var(--ink)' }}>{c.label}</span>
                  <span className="amt" style={{ color: 'var(--ink)' }}>{fmtUSD(c.amount)}</span>
                  <div className="bar-bg">
                    <div className="fill" style={{ width: `${(c.amount / maxCat) * 100}%`, background: categoryColor(c.category) }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card full">
          <div className="card-h">
            <h3>Recent receipts</h3>
            <div className="spacer" />
            <button className="btn ghost sm" onClick={() => navigate('/expenses')}>
              See all <Icons.ChevronRight size={12} />
            </button>
          </div>
          <div className="card-b tight">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Status</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {recentExpenses.map((exp) => (
                  <tr key={exp.id} onClick={() => navigate(`/expenses/${exp.id}`)}>
                    <td className="store">{exp.store_name || 'Expense'}</td>
                    <td className="dt">{fmtDate(exp.transaction_date)}</td>
                    <td>
                      {exp.items[0] ? <CategoryChip category={exp.items[0].category} /> : null}
                      {exp.items.length > 1 ? <span style={{ color: 'var(--muted)', fontSize: 12, marginLeft: 6 }}>+{exp.items.length - 1}</span> : null}
                    </td>
                    <td><StatusBadge status={exp.status} /></td>
                    <td className="num">{fmtUSD(exp.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
